import React, { useState } from "react";

export default function FaqSection({
  faqs,
  title = "Ofte stillede spørgsmål om akupunktur",
  subtitle = "Få svar på de mest almindelige spørgsmål om nænsom behandling hos Kita Akupunktur i Risskov.",
  imageSrc = "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/71339d65-a05d-4c02-a540-2260acdfc245/akupunktur-naal-behandling-arm.jpg",
  imageAlt = "Nænsom akupunkturbehandling med sterile nåle hos Kita Akupunktur i Risskov",
  defaultOpenIndex = -1,
}) {
  const [openIndex, setOpenIndex] = useState(defaultOpenIndex);

  if (!faqs || faqs.length === 0) return null;

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  // Helper to extract plain text for JSON-LD schema
  const getFaqPlainText = (val) => {
    if (typeof val === "string") return val;
    const extract = (node) => {
      if (!node) return "";
      if (typeof node === "string" || typeof node === "number") return String(node);
      if (Array.isArray(node)) return node.map(extract).join(" ");
      if (node && typeof node === "object" && node.props && node.props.children) {
        return extract(node.props.children);
      }
      return "";
    };
    return extract(val);
  };

  // Structured Data for FAQPage
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": getFaqPlainText(f.answer) || f.question,
      },
    })),
  };

  return (
    <div className="w-full bg-transparent text-[#1F1612] py-6 sm:py-10 relative overflow-hidden" aria-labelledby="faq-heading">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Split Layout: ~4:6 column ratio */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
        {/* Left Column: Flush-edged / rounded image filling container */}
        <div className="lg:col-span-5 hidden lg:block relative min-h-[460px] rounded-3xl overflow-hidden shadow-lg border border-[#ffe0cf]">
          <img
            src={imageSrc}
            alt={imageAlt}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        {/* Right Column: FAQ Accordion List */}
        <div className="lg:col-span-7 flex flex-col justify-center">
          {/* Prominent display heading and introductory body paragraph */}
          <div className="mb-6 sm:mb-8">
            <h2 id="faq-heading" className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#111827] font-bold leading-tight" style={{ color: "#111827" }}>
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm sm:text-base text-[#1f2937] font-medium mt-3 leading-relaxed" style={{ color: "#1f2937" }}>
                {subtitle}
              </p>
            )}
          </div>

          {/* Stack of accordion items separated by subtle horizontal border lines */}
          <div className="border-t border-b border-stone-300 divide-y divide-stone-300 text-[#111827]">
            {faqs.map((faq, idx) => {
              const isOpen = openIndex === idx;
              return (
                <div key={idx} className="transition-colors duration-150">
                  <button
                    type="button"
                    onClick={() => toggleFaq(idx)}
                    aria-expanded={isOpen}
                    className="w-full py-4.5 sm:py-5 text-left flex items-center justify-between gap-4 focus:outline-none group cursor-pointer text-[#111827]"
                    style={{ color: "#111827" }}
                  >
                    <span 
                      className="font-serif font-bold text-[#111827] text-base sm:text-lg group-hover:text-[#d74814] transition-colors pr-2"
                      style={{ color: "#111827" }}
                    >
                      {faq.question}
                    </span>
                    <span
                      className={`shrink-0 text-sm text-[#d74814] transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    >
                      <i className="fa-solid fa-chevron-down" aria-hidden="true" />
                    </span>
                  </button>

                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="pb-5 pt-1 text-[#111827] text-sm sm:text-base leading-relaxed pl-2 sm:pl-4">
                        <p className="text-[#111827] font-normal leading-relaxed" style={{ color: "#111827" }}>
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
